import React, { useEffect, useRef } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Divider,
  FormItem,
  Input,
  Modal,
  Toggle,
  Text,
} from 'elmo-elements';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import {
  CreateCategoryType,
  ElaCategoryType,
} from '@/modules/types/elearningCategories';
import {
  useCreateElearningCategoriesMutation,
  useUpdateElearningCategoriesMutation,
} from '@/modules/services';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';
import { useDispatch } from 'react-redux';

interface CategoryModalProps {
  isModalOpen: boolean;
  onSubmit?: (isSuccess: boolean) => void;
  onClose?: () => void;
  categoryData?: ElaCategoryType;
}

const schema = yup.object().shape({
  name: yup
    .string()
    .max(255, 'Maximum of 255 characters only')
    .required('Name is required'),
  isEnabled: yup.boolean().required('Status is required'),
  isActiveByDefault: yup.boolean().optional(),
});

const CategoryModal = ({
  isModalOpen,
  onSubmit = () => {},
  onClose = () => {},
  categoryData,
}: CategoryModalProps): React.ReactElement => {
  const dispatch = useDispatch();
  const nameInputRef = useRef<HTMLInputElement>(null);

  const [createElearningCategories, createElearningCategoriesStatus] =
    useCreateElearningCategoriesMutation();
  const [updateElearningCategories, updateElearningCategoriesStatus] =
    useUpdateElearningCategoriesMutation();

  const {
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
    reset: resetForm,
  } = useForm<CreateCategoryType>({
    resolver: yupResolver(schema),
    defaultValues: {
      isEnabled: categoryData?.isEnabled || false,
      name: categoryData?.name || '',
      isActiveByDefault: categoryData?.isActiveByDefault ?? false,
    },
  });

  const isEnabledWatch = watch('isEnabled');
  const nameWatch = watch('name');
  const isActiveByDefaultWatch = watch('isActiveByDefault');

  const createCategory = async (data: CreateCategoryType) => {
    try {
      const result = await createElearningCategories(data).unwrap();

      if (result) {
        dispatch({
          type: flashMessageActions.addFlashMessage,
          payload: {
            isActive: true,
            message: 'Create Category Successful!',
          },
        });

        onSubmit(true);
        resetForm();
      }
    } catch {
      if (createElearningCategoriesStatus.isError) {
        dispatch({
          type: flashMessageActions.addFlashMessage,
          payload: {
            isActive: true,
            message: 'Create Category Failed! Something went wrong.',
          },
        });

        onSubmit(false);
      }
    }
  };

  const updateCategory = async (data: CreateCategoryType) => {
    try {
      const submitData = {
        name: data.name,
        isEnabled: data.isEnabled,
        isActiveByDefault: data.isActiveByDefault,
        id: categoryData?.id || '',
      };
      const result = await updateElearningCategories(submitData).unwrap();

      if (result) {
        dispatch({
          type: flashMessageActions.addFlashMessage,
          payload: {
            isActive: true,
            message: 'Update Category Successful!',
          },
        });

        onSubmit(true);
        resetForm();
      }
    } catch {
      if (updateElearningCategoriesStatus.isError) {
        dispatch({
          type: flashMessageActions.addFlashMessage,
          payload: {
            isActive: true,
            message: 'Update Category Failed! Something went wrong.',
          },
        });

        onSubmit(false);
      }
    }
  };

  useEffect(() => {
    resetForm(categoryData ?? { name: '', isEnabled: false, isActiveByDefault: false });
  }, [categoryData, isModalOpen]);

  useEffect(() => {
    if (!isEnabledWatch) {
      setValue('isActiveByDefault', false);
    }
  }, [isEnabledWatch]);

  return (
    <Modal
      className="nav-prompt-modal"
      type="large"
      isOpened={isModalOpen}
      closeModal={() => onClose()}
      title={`${categoryData ? 'Edit' : 'Create'} category`}
      primaryButton={
        <div className="nav-prompt">
          <Button type="default" isUppercase={false} onClick={() => onClose()}>
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="button"
            isUppercase={false}
            isDisabled={
              createElearningCategoriesStatus.isLoading ||
              updateElearningCategoriesStatus.isLoading
            }
            onClick={async (e) => {
              const methodToUse = categoryData
                ? updateCategory
                : createCategory;
              await handleSubmit(methodToUse)();
            }}
          >
            Save
          </Button>
        </div>
      }
      closeLabel="Close"
    >
      <Divider />

      <FormItem
        label="Name"
        className="category-padding"
        message={errors.name?.message}
        status={errors.name ? 'error' : 'default'}
      >
        <Input
          htmlType="text"
          ref={nameInputRef}
          placeholder="Enter category Name..."
          value={nameWatch}
          onChange={(e) => {
            setValue('name', e.target.value);

            // This is intentional, Search  has design flaw that it tries to grab focus on every re-render, it has no prop to turn it off.
            setTimeout(() => nameInputRef.current?.focus(), 0);
          }}
        />
      </FormItem>
      <Divider />

      <FormItem
        label="Course Library Access"
        className="category-padding"
        message={errors.isEnabled?.message}
        status={errors.isEnabled ? 'error' : 'default'}
      >
        <Toggle
          isOn={isEnabledWatch}
          onChange={(e) => {
            const isChecked = e.target.checked ? 1 : 0;
            setValue('isEnabled', !!isChecked);
          }}
          onClick={() => {
            /** This is intentional to avoid property missing issue in Toggle component - it's deprecated but still somehow needs onclick in it's props */
          }}
        >
          <div className="category-toggle category-padding">
            <Toggle.Button />
            <div className="category-toggle-info">
              <Text className="toggle-text">Available in Course Library</Text>
              <br />
              <Text size="sm" className="toggle-text">
                if enabled, modules from this category will be available for
                customers to copy from Course Library.
              </Text>
            </div>
          </div>
        </Toggle>
      </FormItem>

      <FormItem>
        <Toggle
          isOn={isActiveByDefaultWatch}
          disabled={!isEnabledWatch}
          onChange={(e) => {
            const isChecked = e.target.checked ? 1 : 0;
            setValue('isActiveByDefault', !!isChecked);
          }}
          onClick={() => {
            /** intentional — avoids property missing issue in Toggle component */
          }}
        >
          <div className="category-toggle category-padding">
            <Toggle.Button />
            <div className="category-toggle-info">
              <Text className="toggle-text">
                Available for all customers by default
              </Text>
              <br />
              <Text size="sm" className="toggle-text">
                If enabled, this category and all modules contained within will
                be available to all customers by default without having to
                manually activate the category.
              </Text>
            </div>
          </div>
        </Toggle>
      </FormItem>

      <Divider />
    </Modal>
  );
};

export default CategoryModal;
